import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "../axios-orders";
import { CompanyReviewSummary, RespondCompanyReviewRequest } from "../types";
import { queryKeys } from "./queryKeys";

/**
 * Public (no-login) company review: a company admin says whether the sender of a quote held in
 * their company's name belongs to it. The token in the emailed link is the whole authorisation, as
 * for the OC link; a logged-out admin has no Bearer header and none is needed.
 */

/** The question the token addresses. 404 = unknown token. Never retried or refetched. */
export const useCompanyReviewDetails = (token: string | undefined) =>
  useQuery<CompanyReviewSummary>({
    queryKey: [queryKeys.COMPANY_REVIEW_TOKEN_DETAILS, token],
    queryFn: async () => {
      const { data } = await axios.post("/company-reviews/token/details", {
        token,
      });
      return data;
    },
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

/** Approve or reject. Already decided (by another admin or our team) answers 409. */
export const useRespondCompanyReview = () =>
  useMutation<CompanyReviewSummary, Error, RespondCompanyReviewRequest>({
    mutationFn: async (body) => {
      const { data } = await axios.post("/company-reviews/token/respond", body);
      return data;
    },
  });
